/**
 * CacApiService — adaptateur du module CAC sur l'API (regroupe les mandats
 * `(société + commissaire + type)` en « rattachements »).
 * Lecture + écriture via MandatCommissaireComptesService / CommissariatAuxComptesService /
 * ReserveCommissariatComptesService + FileService + ReferentielService (résolution code → UUID).
 * Mapping/filtres/dates purs : cf. `cac-api.mappers`. Limites backend (édition mandat,
 * suppleant non-nullable, GET 500, type doc rapport absent).
 */

import { inject, Injectable, signal } from '@angular/core';
import { forkJoin, Observable, of, throwError } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { DrfPaginatedResponse } from '../../../core/models/api.model';
import { type ParameterValueSummary, REFERENTIEL_CODES, type ReferentielCode } from '../../../core/models/gouvernance/referentiel.model';
import { FileService } from '../../../core/services/file.service';
import { PersonneMoraleService } from '../../../core/services/personne-morale.service';
import { PersonnePhysiqueService } from '../../../core/services/personne-physique.service';
import { ReferentielService } from '../../../core/services/referentiel.service';
import { SocieteService } from '../../../core/services/societe.service';
import { MAX_PAGE_SIZE } from '../../../shared/core/services/pagination.util';
import { collectUploadedId, fetchAll, rollbackUploads } from '../../../shared/rx';
import { CommissariatAuxComptesService } from './services/commissariat-aux-comptes.service';
import { MandatCacLectureService } from './services/mandat-cac-lecture.service';
import { type MandatCacListFilters, MandatCommissaireComptesService } from './services/mandat-commissaire-comptes.service';
import { ReserveCommissariatComptesService } from './services/reserve-commissariat-comptes.service';
import { type LigneImportReserve, validerLigneReserve, versReservesNested } from './cac-reserves-import.helpers';
import { type CategorieSociete, type OpinionCac, TYPES_ENTITE_CAC } from './models/cac.enums';
import type { MandatCommissaireAuxComptes } from './models/cac-api.model';
import type {
    AlerteMandat,
    DocumentExerciceCac,
    ExerciceCac,
    ExerciceCacCreate,
    MandatCac,
    MandatCacCreate,
    MandatDetailleCac,
    PersonneMoraleRef,
    PersonnePhysiqueRef,
    RattachementCac,
    ReserveCac,
    ReserveCacUpdate,
    SocieteRef,
} from './models/cac.model';
import {
    buildDocs,
    codeDocumentReel,
    type ExerciceCacFilters,
    matchExercice,
    matchRattachement,
    patchPourSlot,
    type RattachementCacFilters,
    rattachementsActifs,
    toDoc,
    toExercice,
    toMandatCac,
    toRattachement,
    toReserve,
    type TypeDocumentExercice,
} from './cac-api.mappers';

export type { ExerciceCacFilters, RattachementCacFilters, TypeDocumentExercice };

// Helpers d'import matrice réserves (modèle Excel, parsing, validation) : cf. `cac-reserves-import.helpers`.
export { COLONNES_TEMPLATE_RESERVE } from './cac-reserves-import.helpers';
export type { LigneImportReserve } from './cac-reserves-import.helpers';

export type SoumissionReserveResult = { inserees: number; remplacees: number };

export { TYPES_ENTITE_CAC };

@Injectable({ providedIn: 'root' })
export class CacApiService {
    private readonly mandatService = inject(MandatCommissaireComptesService);
    private readonly mandatLecture = inject(MandatCacLectureService);
    private readonly commissariatService = inject(CommissariatAuxComptesService);
    private readonly reserveService = inject(ReserveCommissariatComptesService);
    private readonly societeService = inject(SocieteService);
    private readonly ppService = inject(PersonnePhysiqueService);
    private readonly pmService = inject(PersonneMoraleService);
    private readonly fileService = inject(FileService);
    private readonly referentiel = inject(ReferentielService);

    /** Tampon d'import matrice réserves (purement client). */
    private readonly importReserveBuffer = signal<{ exerciceId: string; lignes: LigneImportReserve[] } | null>(null);

    /* ── Référentiels d'entités (dropdowns) ─────────────────────────────── */

    listSocietes(): Observable<SocieteRef[]> {
        return this.societeService.listAllCached().pipe(
            map((resp) =>
                resp.results.map((s): SocieteRef => ({
                    societe_id: s.id,
                    denomination: s.denomination,
                    sigle: s.sigle,
                    categorie_societe: (s.categorie_societe?.code ?? 'SODE') as CategorieSociete,
                })),
            ),
        );
    }

    /** Sociétés éligibles à un rattachement CAC (filtre par `type_societe` = TYPE_ENTITE, cf. TYPES_ENTITE_CAC). */
    listSocietesEligiblesCac(): Observable<SocieteRef[]> {
        return this.societeService.listAllCached().pipe(
            map((resp) =>
                resp.results
                    .filter((s) => TYPES_ENTITE_CAC.includes(s.type_societe?.code ?? ''))
                    .map((s): SocieteRef => ({
                        societe_id: s.id,
                        denomination: s.denomination,
                        sigle: s.sigle,
                        categorie_societe: (s.categorie_societe?.code ?? 'SODE') as CategorieSociete,
                    })),
            ),
        );
    }

    listCommissaires(): Observable<PersonnePhysiqueRef[]> {
        return fetchAll((page) => this.ppService.list({ page, per_page: MAX_PAGE_SIZE })).pipe(
            map((resp) =>
                resp.results.map((p): PersonnePhysiqueRef => ({
                    personne_id: p.id,
                    nom: p.nom,
                    prenom: p.prenoms,
                })),
            ),
        );
    }

    /**
     * Toutes les personnes morales, avec leur `type_code` (`TYPE_PERSONNE_MORALE`).
     * Liste complète car elle sert aussi à RÉSOUDRE le nom du cabinet d'un mandat
     * existant (tous types confondus). Le FILTRAGE aux cabinets éligibles
     * (CABINET / CABINET_AUDIT / CABINET_EXPERT_COMPTABLE) est fait côté champ (Fiche 360°).
     */
    listCabinets(): Observable<PersonneMoraleRef[]> {
        return fetchAll((page) => this.pmService.list({ page, per_page: MAX_PAGE_SIZE })).pipe(
            map((resp) =>
                resp.results.map((m): PersonneMoraleRef => ({
                    personne_id: m.id,
                    raison_sociale: m.denomination,
                    sigle: m.sigle,
                    type_code: m.type_personne_morale?.code,
                })),
            ),
        );
    }

    /* ── Rattachements (regroupement client des mandats) ─────────────────── */

    listRattachements(filters: RattachementCacFilters = {}): Observable<DrfPaginatedResponse<RattachementCac>> {
        // Société et échéance partent au serveur — `societe` est servi depuis la refonte
        // d'octobre 2026, ce qui évite de charger tout le portefeuille pour une seule fiche
        // (BE-082 résolue). Restent client la recherche libre et la nature du mandataire, que
        // l'API n'expose pas, ainsi que le regroupement des reconductions.
        const requete = {
            ...(filters.societe ? { societe: filters.societe } : {}),
            ...(filters.date_fin__gte ? { date_fin__gte: filters.date_fin__gte } : {}),
        };
        return fetchAll((page) => this.mandatService.list({ ...requete, page, per_page: MAX_PAGE_SIZE })).pipe(
            map((resp) => {
                let rattachements = resp.results.map(toRattachement);
                rattachements = rattachements.filter((r) => matchRattachement(r, filters));
                const page = filters.page ?? 1;
                const perPage = filters.per_page ?? 25;
                const total = rattachements.length;
                const start = (page - 1) * perPage;
                return {
                    count: total,
                    next: null,
                    previous: null,
                    results: rattachements.slice(start, start + perPage),
                };
            }),
        );
    }

    getRattachement(id: string): Observable<RattachementCac> {
        return this.mandatService.retrieve(id).pipe(map(toRattachement));
    }

    /**
     * Mandats successifs d'un rattachement — ses reconductions, du plus récent au plus ancien.
     *
     * Les mandats d'une même paire se retrouvent au serveur, par société et par porteur titulaire
     * (`personne_physique_titulaire` ou `cabinet_titulaire`). Sans porteur identifiable, le mandat
     * est seul : rien ne permettrait de lui rattacher les autres sans risquer d'en agréger
     * d'étrangers.
     */
    listMandats(rattachementId?: string): Observable<MandatCac[]> {
        if (!rattachementId) {
            return fetchAll((page) => this.mandatService.list({ page, per_page: MAX_PAGE_SIZE })).pipe(map((resp) => this.versMandats(resp.results)));
        }
        return this.mandatService.retrieve(rattachementId).pipe(
            switchMap((repr) => {
                const porteur = repr.personne_physique_titulaire
                    ? { personne_physique_titulaire: repr.personne_physique_titulaire.id }
                    : repr.cabinet_titulaire
                      ? { cabinet_titulaire: repr.cabinet_titulaire.id }
                      : null;
                if (!porteur) {
                    return of([repr]);
                }
                return fetchAll((page) => this.mandatService.list({ societe: repr.societe, ...porteur, page, per_page: MAX_PAGE_SIZE })).pipe(
                    map((resp) => (resp.results.length > 0 ? resp.results : [repr])),
                );
            }),
            map((mandats) => this.versMandats(mandats, rattachementId)),
        );
    }

    private versMandats(mandats: MandatCommissaireAuxComptes[], rattachementId?: string): MandatCac[] {
        return mandats.map((m) => toMandatCac(m, rattachementId ?? m.id)).sort((a, b) => b.rang_mandat - a.rang_mandat);
    }

    /**
     * Reconduction d'un mandat : nouveau mandat répliquant **la paire** du mandat d'origine
     * (titulaire et suppléant, avec leurs cabinets et natures de mandataire), `date_designation` =
     * début saisi ; le backend recalcule durée, période et rang.
     *
     * Les remplaçants ne sont pas reconduits : un nouveau mandat part de la désignation d'origine,
     * le remplacement se refait au besoin par PATCH (action « Remplacer » de la liste).
     */
    createMandat(payload: MandatCacCreate): Observable<MandatCac> {
        return this.mandatService.retrieve(payload.rattachement_id).pipe(
            switchMap((orig) =>
                this.mandatService
                    .create({
                        // Chaque rôle est porté par une personne physique OU un cabinet : la clé est
                        // omise plutôt qu'envoyée vide (la chaîne vide est refusée par le serveur).
                        ...(orig.personne_physique_titulaire ? { personne_physique_titulaire_id: orig.personne_physique_titulaire.id } : {}),
                        ...(orig.cabinet_titulaire ? { cabinet_titulaire_id: orig.cabinet_titulaire.id } : {}),
                        type_mandataire_titulaire_id: orig.type_mandataire_titulaire.id,
                        ...(orig.personne_physique_suppleant ? { personne_physique_suppleant_id: orig.personne_physique_suppleant.id } : {}),
                        ...(orig.cabinet_suppleant ? { cabinet_suppleant_id: orig.cabinet_suppleant.id } : {}),
                        ...(orig.type_mandataire_suppleant ? { type_mandataire_suppleant_id: orig.type_mandataire_suppleant.id } : {}),
                        societe_id: orig.societe,
                        type_acte_nomination_id: orig.type_acte_nomination.id,
                        date_designation: payload.date_debut_mandat,
                        reference_acte_nomination: payload.reference_acte_nomination ?? orig.reference_acte_nomination,
                    })
                    .pipe(map((m) => toMandatCac(m, payload.rattachement_id))),
            ),
        );
    }

    /* ── Alertes (dérivées de date_fin, pas d'endpoint dédié) ────────────── */

    listAlertesMandats(): Observable<AlerteMandat[]> {
        return this.mandatLecture.listAlertes();
    }

    /** Mandats un par un, suppléant résolu (cf. `MandatCacLectureService`). */
    listMandatsDetailles(filters: MandatCacListFilters = {}): Observable<MandatDetailleCac[]> {
        return this.mandatLecture.listDetailles(filters);
    }

    /* ── Exercices (commissariat) ────────────────────────────────────────── */

    listExercices(filters: ExerciceCacFilters = {}): Observable<DrfPaginatedResponse<ExerciceCac>> {
        // Année et opinion filtrées côté serveur ; société (aucun filtre serveur) et recherche libre restent client.
        // Mandats chargés en parallèle pour dériver `commissaires_actifs_ids` (lien absent du backend).
        const { annee, opinion } = filters;
        return forkJoin({
            commissariats: fetchAll((page) =>
                this.commissariatService.list({ page, per_page: MAX_PAGE_SIZE, annee_debut: annee, opinion_formulee__code: opinion }),
            ),
            mandats: fetchAll((page) => this.mandatService.list({ page, per_page: MAX_PAGE_SIZE })),
        }).pipe(
            map(({ commissariats, mandats }) => {
                let exercices = commissariats.results
                    .map((c) => {
                        const e = toExercice(c);
                        e.commissaires_actifs_ids = rattachementsActifs(mandats.results, e.societe_id, e.annee_exercice);
                        return e;
                    })
                    .filter((e) => matchExercice(e, filters));
                const page = filters.page ?? 1;
                const perPage = filters.per_page ?? 25;
                const total = exercices.length;
                const start = (page - 1) * perPage;
                exercices = exercices.slice(start, start + perPage);
                return { count: total, next: null, previous: null, results: exercices };
            }),
        );
    }

    getExercice(id: string): Observable<ExerciceCac> {
        // Voir `listExercices` : mandats joints pour dériver les commissaires actifs sur l'exercice.
        return forkJoin({
            commissariat: this.commissariatService.retrieve(id),
            mandats: fetchAll((page) => this.mandatService.list({ page, per_page: MAX_PAGE_SIZE })),
        }).pipe(
            map(({ commissariat, mandats }) => {
                const e = toExercice(commissariat);
                e.commissaires_actifs_ids = rattachementsActifs(mandats.results, e.societe_id, e.annee_exercice);
                return e;
            }),
        );
    }

    /** Valide l'exercice (action `demarrer_suivi`) puis renvoie l'exercice rechargé (lecture seule). */
    validerExercice(id: string): Observable<ExerciceCac> {
        return this.commissariatService.demarrerSuivi(id).pipe(switchMap(() => this.getExercice(id)));
    }

    /**
     * Création d'un exercice. Le backend n'accepte que `societe_id` + années à la
     * création ; l'année unique est mappée sur `annee_debut`=`annee_fin`.
     * L'opinion/observation éventuelles sont posées via un PATCH consécutif. La réponse
     * de création étant tronquée (id, société, années), l'exercice est rechargé complet.
     */
    createExercice(payload: ExerciceCacCreate): Observable<ExerciceCac> {
        return this.commissariatService
            .create({ societe_id: payload.societe_id, annee_debut: payload.annee_exercice, annee_fin: payload.annee_exercice })
            .pipe(
                switchMap((created) => {
                    const besoinPatch = Boolean(payload.opinion_formulee) || Boolean(payload.observation && payload.observation.trim());
                    if (!besoinPatch) {
                        return this.getExercice(created.id);
                    }
                    return this.updateExercice(created.id, {
                        opinion_formulee: payload.opinion_formulee ?? null,
                        observation: payload.observation ?? null,
                    });
                }),
            );
    }

    /**
     * Mise à jour exercice (opinion + observation). Opinion résolue code → UUID.
     * `observation` n'est envoyée que non vide : le champ est `blank=False` côté modèle
     * (ni chaîne vide ni `null` acceptés) → une observation déjà saisie ne peut pas être effacée.
     */
    updateExercice(id: string, patch: { opinion_formulee?: OpinionCac | null; observation?: string | null }): Observable<ExerciceCac> {
        const observation = patch.observation?.trim();
        return this.resolveId(REFERENTIEL_CODES.OPINION_FORMULEE_COMMISSARIAT_AUX_COMPTES, patch.opinion_formulee).pipe(
            switchMap((opinionId) =>
                this.commissariatService.partialUpdate(id, {
                    ...(patch.opinion_formulee !== undefined ? { opinion_formulee_id: opinionId } : {}),
                    ...(observation ? { observation } : {}),
                }),
            ),
            map((c) => toExercice(c)),
        );
    }

    /* ── Documents d'exercice (champs File du commissariat) ──────────────── */

    listDocumentsExercice(exerciceId: string): Observable<DocumentExerciceCac[]> {
        return this.commissariatService.retrieve(exerciceId).pipe(map((c) => buildDocs(c)));
    }

    /**
     * Ajout d'un document à l'exercice : upload `/files/` puis rattachement au bon
     * slot du commissariat (lettre mission / matrice / plan d'action / rapports[]).
     * NB : le référentiel `TYPE_DOCUMENT_COMMISSARIAT_AUX_COMPTES` ne couvre pas les
     * types de « rapport d'élaboration » → un rapport est
     * uploadé avec le type générique disponible et ajouté à `documents_rapport_elaboration`.
     */
    ajouterDocumentExercice(exerciceId: string, type: TypeDocumentExercice, file: File): Observable<DocumentExerciceCac> {
        const docCode = codeDocumentReel(type);
        return forkJoin({
            commissariat: this.commissariatService.retrieve(exerciceId),
            typeDocId: this.resolveId(REFERENTIEL_CODES.TYPE_DOCUMENT_COMMISSARIAT_AUX_COMPTES, docCode),
        }).pipe(
            switchMap(({ commissariat, typeDocId }) => {
                // Jamais de type vide envoyé à /files/ (audit R-25).
                if (!typeDocId) {
                    return throwError(() => new Error('Type de document introuvable.'));
                }
                return this.deposerDocument(exerciceId, commissariat.societe, typeDocId, file, type);
            }),
        );
    }

    /**
     * Ajout d'un des « 13 rapports du commissariat » (référentiel TREIZE_RAPPORT_CAC) :
     * le fichier est typé directement par l'UUID de la valeur de référentiel choisie
     * (`type_document_id`), puis ajouté au slot multi-fichiers `documents_rapport_elaboration`.
     * Contrairement à `ajouterDocumentExercice`, le type n'est pas résolu via
     * `TYPE_DOCUMENT_COMMISSARIAT_AUX_COMPTES` : chaque rapport reste distinct par son type.
     */
    ajouterRapportElaboration(exerciceId: string, typeRapportId: string, file: File): Observable<DocumentExerciceCac> {
        return this.commissariatService
            .retrieve(exerciceId)
            .pipe(switchMap((commissariat) => this.deposerDocument(exerciceId, commissariat.societe, typeRapportId, file, 'RAPPORT_AUTRE')));
    }

    /* ── Réserves ────────────────────────────────────────────────────────── */

    listReserves(exerciceId: string): Observable<ReserveCac[]> {
        return fetchAll((page) => this.reserveService.list({ commissariat_aux_comptes_id: exerciceId, page, per_page: MAX_PAGE_SIZE })).pipe(
            map((resp) => resp.results.map((r) => toReserve(r))),
        );
    }

    /**
     * Validation DSE d'une réserve (catégorie résolue code → UUID, reformulation, récurrence).
     * `reformulation` n'est envoyée que non vide (`blank=False` côté modèle) : vider le champ
     * laisse la valeur existante intacte. La réponse du PATCH est tronquée (ni intitulé, ni
     * montant, ni exercice parent) : rien n'est restitué, la liste est rechargée par l'écran.
     */
    validerReserveDse(id: string, patch: ReserveCacUpdate): Observable<void> {
        const reformulation = patch.reformulation?.trim();
        return this.resolveId(REFERENTIEL_CODES.CATEGORIE_RESERVE_COMMISSARIAT_AUX_COMPTES, patch.categorie).pipe(
            switchMap((categorieId) =>
                this.reserveService.partialUpdate(id, {
                    ...(patch.categorie !== undefined && categorieId ? { categorie_id: categorieId } : {}),
                    ...(reformulation ? { reformulation } : {}),
                    ...(patch.recurrence !== undefined ? { recurrence: patch.recurrence } : {}),
                }),
            ),
            map(() => undefined),
        );
    }

    /**
     * Soumission de l'import matrice : remplace les réserves de l'exercice via le PATCH nested du
     * commissariat (`reserves[]`). La matrice porte les 5 colonnes (intitulé, reformulation,
     * montant, catégorie, récurrence) ; la catégorie (libellé/code) est résolue code → UUID.
     */
    soumettreImportReserves(exerciceId: string, lignes: LigneImportReserve[]): Observable<SoumissionReserveResult> {
        return forkJoin({
            commissariat: this.commissariatService.retrieve(exerciceId),
            categories: this.getCategoriesReserve(),
        }).pipe(
            switchMap(({ commissariat, categories }) => {
                if (commissariat.valide) {
                    return throwError(() => new Error('Exercice validé : réserves verrouillées.'));
                }
                const enErreur = lignes.filter((l) => validerLigneReserve(l, categories).length > 0);
                if (enErreur.length > 0) {
                    return throwError(() => new Error(`${enErreur.length} ligne(s) encore en erreur — corrigez avant soumission.`));
                }
                const remplacees = commissariat.reserves?.length ?? 0;
                const reserves = versReservesNested(lignes, categories);
                return this.commissariatService.partialUpdate(exerciceId, { reserves }).pipe(map(() => ({ inserees: reserves.length, remplacees })));
            }),
        );
    }

    /** Valeurs du référentiel CATEGORIE_RESERVE_COMMISSARIAT_AUX_COMPTES (catégories de réserve). */
    getCategoriesReserve(): Observable<ParameterValueSummary[]> {
        return this.referentiel.getValues(REFERENTIEL_CODES.CATEGORIE_RESERVE_COMMISSARIAT_AUX_COMPTES);
    }

    setImportReserveBuffer(exerciceId: string, lignes: LigneImportReserve[]): void {
        this.importReserveBuffer.set({ exerciceId, lignes });
    }

    getImportReserveBuffer(): { exerciceId: string; lignes: LigneImportReserve[] } | null {
        return this.importReserveBuffer();
    }

    clearImportReserveBuffer(): void {
        this.importReserveBuffer.set(null);
    }

    /** Résout l'UUID d'une valeur de référentiel à partir de son code (null si absent). */
    private resolveId(referentielCode: ReferentielCode, valeurCode: string | null | undefined): Observable<string | null> {
        if (!valeurCode) {
            return of(null);
        }
        return this.referentiel.getValues(referentielCode).pipe(map((vals) => vals.find((v) => v.code === valeurCode)?.id ?? null));
    }

    /** Upload puis rattachement ; patch construit sur une RELECTURE faite après l'upload (audit R-10). Échec → fichier supprimé. */
    private deposerDocument(
        exerciceId: string,
        societeId: string,
        typeDocId: string,
        file: File,
        type: TypeDocumentExercice,
    ): Observable<DocumentExerciceCac> {
        const uploaded: string[] = [];
        return this.fileService.upload(file, typeDocId, { societeId }).pipe(
            collectUploadedId(uploaded),
            switchMap((doc) =>
                this.commissariatService.retrieve(exerciceId).pipe(
                    switchMap((frais) => this.commissariatService.partialUpdate(exerciceId, patchPourSlot(type, doc.id, frais))),
                    map(() => toDoc(exerciceId, type, doc.name, doc.id, doc.created_at)),
                ),
            ),
            rollbackUploads(uploaded, (id) => this.fileService.supprimerOrphelin(id)),
        );
    }
}
